import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileSignature, RefreshCw, Send, Eye, Download, Loader2, ExternalLink } from 'lucide-react';

const STATUS_STYLES = {
  Generated: 'bg-blue-100 text-blue-800 border-blue-200',
  'Sent for Signature': 'bg-amber-100 text-amber-800 border-amber-200',
  Signed: 'bg-green-100 text-green-800 border-green-200',
  'Rejected/Cancelled': 'bg-red-100 text-red-800 border-red-200',
  Cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

/**
 * Agreement section shown inside the Driver View dialog.
 * Handles: list, generate PDF, view/download, send for eSign.
 */
export default function DriverAgreementSection({ API, driver }) {
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [config, setConfig] = useState({ configured: false, provider: 'digio' });
  const [busy, setBusy] = useState(null); // agreement_id currently being acted on
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (!driver?.id) return;
    try {
      setLoading(true);
      const [aRes, cRes] = await Promise.all([
        axios.get(`${API}/agreements/driver/${driver.id}`),
        axios.get(`${API}/agreements/config`),
      ]);
      setAgreements(aRes.data || []);
      setConfig(cRes.data || {});
    } catch (e) {
      // silent — endpoints may not exist during first migration
      setAgreements([]);
    } finally {
      setLoading(false);
    }
  }, [API, driver?.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!driver?.id) return;
    try {
      setGenerating(true);
      await axios.post(`${API}/agreements/generate/${driver.id}`);
      toast.success('Agreement generated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate agreement');
    } finally {
      setGenerating(false);
    }
  };

  const openPdf = async (agreement, signed = false) => {
    try {
      setBusy(agreement.agreement_id);
      const { data } = await axios.get(
        `${API}/agreements/${agreement.agreement_id}/pdf`,
        { params: { signed } }
      );
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open PDF');
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async (agreement, signed = false) => {
    try {
      setBusy(agreement.agreement_id);
      const { data } = await axios.get(
        `${API}/agreements/${agreement.agreement_id}/pdf`,
        { params: { signed } }
      );
      if (!data?.url) throw new Error('No URL returned');
      const a = document.createElement('a');
      a.href = data.url;
      a.download = `${agreement.agreement_id}${signed ? '-signed' : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download PDF');
    } finally {
      setBusy(null);
    }
  };

  const sendForEsign = async (agreement) => {
    if (!config.configured) {
      toast.error('eSign provider not configured');
      return;
    }
    if (!window.confirm(`Send Agreement ${agreement.agreement_id} to ${driver.name} (${driver.mobile}) for Aadhaar eSign?`)) return;
    try {
      setBusy(agreement.agreement_id);
      const { data } = await axios.post(`${API}/agreements/${agreement.agreement_id}/send-esign`);
      toast.success('eSign request created');
      if (data?.sign_url) {
        window.open(data.sign_url, '_blank', 'noopener,noreferrer');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send for eSign');
    } finally {
      setBusy(null);
    }
  };

  const latestSigned = agreements.find(a => a.status === 'Signed');
  const activeUnsigned = agreements.find(a => a.status === 'Generated' || a.status === 'Sent for Signature');

  return (
    <div className="border-t pt-4" data-testid="driver-agreement-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-slate-600" />
          <h4 className="font-semibold">Agreement</h4>
        </div>
        <div className="flex items-center gap-2">
          {!config.configured && (
            <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="esign-not-configured">
              eSign not configured
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="agreement-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading agreements…
        </div>
      ) : agreements.length === 0 ? (
        <div className="p-4 bg-gray-50 rounded-md flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700">No agreement generated yet.</p>
            <p className="text-xs text-gray-500 mt-1">
              Auto-populated from onboarding data — no re-entry required.
            </p>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            data-testid="generate-agreement-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><FileSignature className="w-4 h-4 mr-2" /> Generate Agreement</>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Highlight card for signed or active */}
          {latestSigned && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md" data-testid="signed-agreement-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-sm text-green-900">
                      {latestSigned.agreement_id}
                    </span>
                    <Badge className="bg-green-600 text-white">SIGNED ✓</Badge>
                  </div>
                  <p className="text-xs text-green-800 mt-1">
                    Version {latestSigned.template_version} · Signed on {formatDate(latestSigned.signed_at)}
                  </p>
                  {latestSigned.provider_document_id && (
                    <p className="text-[11px] text-green-700 mt-0.5 font-mono">
                      Ref: {latestSigned.provider_document_id}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPdf(latestSigned, true)}
                    disabled={busy === latestSigned.agreement_id}
                    data-testid="view-signed-agreement-btn"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => downloadPdf(latestSigned, true)}
                    disabled={busy === latestSigned.agreement_id}
                    data-testid="download-signed-agreement-btn"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!latestSigned && activeUnsigned && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md" data-testid="active-agreement-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-sm">{activeUnsigned.agreement_id}</span>
                    <Badge className={STATUS_STYLES[activeUnsigned.status] || 'bg-gray-100 text-gray-800'}>
                      {activeUnsigned.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Version {activeUnsigned.template_version} · Generated on {formatDate(activeUnsigned.generated_at)}
                  </p>
                  {activeUnsigned.provider_sign_url && (
                    <a
                      href={activeUnsigned.provider_sign_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mt-1"
                      data-testid="open-signer-link"
                    >
                      <ExternalLink className="w-3 h-3" /> Open signer link
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPdf(activeUnsigned, false)}
                    disabled={busy === activeUnsigned.agreement_id}
                    data-testid="preview-draft-agreement-btn"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                  {activeUnsigned.status !== 'Sent for Signature' && (
                    <Button
                      size="sm"
                      onClick={() => sendForEsign(activeUnsigned)}
                      disabled={busy === activeUnsigned.agreement_id || !config.configured}
                      data-testid="send-for-signature-btn"
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> Send for Signature
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Regenerate option is only available when nothing is active */}
          {!activeUnsigned && !latestSigned && (
            <Button onClick={handleGenerate} disabled={generating} data-testid="generate-agreement-btn">
              <FileSignature className="w-4 h-4 mr-2" /> Generate Agreement
            </Button>
          )}

          {/* Full history */}
          {agreements.length > 1 && (
            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer select-none">
                Show all {agreements.length} agreement records
              </summary>
              <div className="mt-2 space-y-1">
                {agreements.map(a => (
                  <div
                    key={a.agreement_id}
                    className="flex items-center justify-between px-2 py-1 rounded border bg-white"
                    data-testid={`agreement-row-${a.agreement_id}`}
                  >
                    <span className="font-mono">{a.agreement_id}</span>
                    <span>v{a.template_version}</span>
                    <Badge className={STATUS_STYLES[a.status] || 'bg-gray-100 text-gray-700'}>{a.status}</Badge>
                    <span>{formatDate(a.generated_at)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
